import type { Industry } from "@/types";
import { SITE_URL } from "@/lib/constants";

export const industries: Industry[] = [
  {
    id: "food-beverage",
    slug: "food-beverage-erp-software",
    title: "Smart ERP for Food Processing & Beverage Manufacturing",
    shortLabel: "Food & Beverage",
    tagline: "From recipe to retail — full batch traceability",
    description: "Manage recipes, batch production, and strict compliance for the food and beverage industry.",
    longDescription: "Our Food & Beverage ERP Software ensures safety, compliance, and efficiency from raw material procurement to final distribution — with batch traceability, expiry tracking, and FSSAI compliance built in.",
    image: "/images/industries/food-bev.jpg",
    icon: "Utensils",
    href: "/industries/food-beverage-erp-software",
    color: "#059A8F",
    secondaryColor: "#047857",
    category: "FOOD & BEVERAGE",
    tags: ["Mx-ERP", "F&B", "Traceability"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Without a modern ERP system, F&B companies struggle with compliance risks, wastage, and inefficiencies.",
    painPoints: [
      { icon: "Layers", title: "Batch Traceability Issues", description: "Difficulty tracking raw materials and finished goods batches across the supply chain." },
      { icon: "Clock", title: "Expiry & Waste Management", description: "High wastage and inventory losses due to poor expiry tracking and stock rotation." },
      { icon: "FileCheck", title: "Quality Compliance Risks", description: "Manual quality checks leading to compliance failures and audit challenges." },
      { icon: "Calculator", title: "Inventory Inaccuracy", description: "Stock mismatches causing production delays and operational inefficiencies." },
      { icon: "Thermometer", title: "Disconnected Operations", description: "Lack of synchronization between procurement, production, and sales processes." }
    ],
    capabilitiesHeading: "Solve Every Food Industry Challenge with Mx-ERP",
    capabilities: [
      {
        icon: "Book",
        title: "Batch & Lot Traceability",
        description: "Track every ingredient and finished product with complete backward and forward traceability.",
        features: ["Ingredient Tracking", "Lot Management", "Product Traceability"]
      },
      {
        icon: "RefreshCw",
        title: "Expiry & Shelf-Life Management",
        description: "Automated expiry alerts to reduce wastage and ensure compliance.",
        features: ["Expiry Alerts", "Shelf-Life Monitoring", "Waste Reduction"]
      },
      {
        icon: "Calendar",
        title: "Quality Control & Compliance",
        description: "Built-in FSSAI compliance, QC workflows, and comprehensive audit trails.",
        features: ["FSSAI Compliance", "QC Workflows", "Audit Trails"]
      },
      {
        icon: "CheckSquare",
        title: "Production Planning & Control",
        description: "Optimize recipes, BOM, and production scheduling for maximum efficiency.",
        features: ["Recipe Management", "BOM Planning", "Production Scheduling"]
      },
      {
        icon: "BarChart3",
        title: "Inventory & Warehouse Management",
        description: "Real-time stock tracking and inventory visibility across multiple warehouses.",
        features: ["Stock Tracking", "Multi-Warehouse Support", "Inventory Visibility"]
      },
      {
        icon: "Truck",
        title: "Integrated Finance & GST",
        description: "Seamless accounting, GST compliance, and financial reporting in one platform.",
        features: ["GST Compliance", "Financial Reporting", "Integrated Accounting"]
      }
    ],
    stats: [
      { value: "35%", label: "increase in operational efficiency" },
      { value: "60%", label: "reduction in quality defects" },
      { value: "1000+", label: "food businesses served" }
    ],
    caseStudy: {
      quote: "The batch tracking system is a lifesaver for our FSSAI audits.",
      author: "Sneha Mehta",
      role: "Quality Manager",
      company: "FreshFoods Ltd",
      location: "Bangalore, India",
      metrics: [
        { value: "45%", label: "waste reduction", trend: "up" },
        { value: "100%", label: "traceability", trend: "up" },
        { value: "22%", label: "margin improvement", trend: "up" }
      ]
    },
    relatedIndustries: ["packaging", "chemical", "hospitality"],
    dashboard: {
      title: "F&B Operations Overview",
      subtitle: "Live data from Plant A and Warehouse B",
      kpis: [
        { label: "BATCH REVENUE", value: "₹45.2L", trend: "↗ +12.5%", icon: "TrendingUp", color: "#10b981" },
        { label: "ACTIVE BATCHES", value: "12,450", trend: "↗ +4.1%", icon: "FlaskConical", color: "#3b82f6" },
        { label: "PENDING ORDERS", value: "142", trend: "↘ -2.5%", icon: "Package", color: "#f59e0b" },
        { label: "EXPIRY ALERTS", value: "28", trend: "Requires Action", icon: "AlertTriangle", color: "#ef4444" }
      ],
      chartTitle: "QUALITY GROWTH",
      linePath: "M0,22 C15,24 25,16 40,15 C50,14 55,10 65,8 C75,5 85,4 100,3",
      areaPath: "M0,22 C15,24 25,16 40,15 C50,14 55,10 65,8 C75,5 85,4 100,3 L100,30 L0,30 Z",
      lineColor: "#059A8F",
      barTitle: "BATCH VOLUME",
      barColor: "#059A8F",
      barHeights: [72, 65, 48, 80, 55, 90, 68],
      sidebarItems: [
        { label: "Batch Control", icon: "FlaskConical" },
        { label: "Inventory", icon: "Package" },
        { label: "Quality Lab", icon: "CheckSquare" },
        { label: "Analytics", icon: "BarChart3" },
        { label: "GST Reports", icon: "FileText" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Modern Food Industry",
      description: "Advanced IoT and AI tools to ensure food safety and production efficiency.",
      items: [
        {
          title: "Real-time Analytics",
          desc: "Live dashboards for production, inventory, and sales performance.",
          icon: "Thermometer"
        },
        {
          title: "IoT & Machine Integration",
          desc: "Connect with processing machines for automated production tracking.",
          icon: "Zap"
        },
        {
          title: "Supply Chain Integration",
          desc: "Seamless coordination with vendors, distributors, and logistics partners.",
          icon: "ShieldCheck"
        }
      ]
    },
    faqs: [
      {
        question: "What is Food & Beverage ERP Software?",
        answer: "Food ERP software helps manage production, inventory, quality, compliance, and distribution in a single integrated system."
      },
      {
        question: "Does it support FSSAI compliance?",
        answer: "Yes, it includes built-in compliance tools, audit tracking, regulatory workflows, and reporting to help meet FSSAI requirements."
      },
      {
        question: "Can it handle batch traceability?",
        answer: "Yes, it provides complete batch and lot traceability across the supply chain, from raw materials to finished products."
      },
      {
        question: "Is it suitable for small manufacturers?",
        answer: "Yes, Mx-ERP is scalable and designed to support startups, SMEs, and large food manufacturing enterprises."
      },
      {
        question: "Does it reduce wastage?",
        answer: "Yes, with expiry tracking, shelf-life management, and inventory optimization, wastage can be significantly reduced."
      }
    ],
    seo: {
      title: "Food Industry | Food & Beverage ERP Software - ACGIL",
      description: "Food and beverage ERP software covering production, inventory, procurement, sales, costing, finance, and operational processes for food industry businesses.",
      keywords: "food and beverage erp software, f&b erp software, food manufacturing erp, batch traceability software, expiry management system, fssai compliance software, production erp for food industry, inventory management food erp",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/food-beverage-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/food-beverage-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/food-beverage-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "Food Industry | Food & Beverage ERP Software - ACGIL",
        description: "Food and beverage ERP software covering production, inventory, procurement, sales, costing, finance, and operational processes for food industry businesses.",
        url: SITE_URL + "/industries/food-beverage-erp-software",
        type: "website",
        image: SITE_URL + "/images/food-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Food & Beverage ERP Software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Food & Beverage ERP Software helps manage production, inventory, batch traceability, quality control, and compliance in one integrated system."
      }
    },
    {
      "@type": "Question",
      "name": "Why do food manufacturers need ERP software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It improves efficiency, ensures compliance, reduces wastage, and provides real-time visibility across operations."
      }
    },
    {
      "@type": "Question",
      "name": "Does it support batch traceability?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it provides complete backward and forward batch traceability for raw materials and finished goods."
      }
    },
    {
      "@type": "Question",
      "name": "Can it manage expiry and shelf life?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it includes automated expiry tracking and shelf-life management to reduce wastage."
      }
    },
    {
      "@type": "Question",
      "name": "Is it compliant with FSSAI regulations?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it includes built-in compliance tools, audit trails, and reporting as per FSSAI standards."
      }
    }
  ]
}`}
  },
  {
    id: "electrical",
    slug: "electrical-erp-software",
    title: "Electrical & Electronics ERP Software",
    shortLabel: "Electrical & Electronics",
    tagline: "Smart ERP for Electronics Manufacturing & Assembly Industry",
    description: "Our Electrical & Electronics ERP Software streamlines operations from component sourcing to final product delivery — with BOM management, production control, quality testing, and compliance tracking built in.",
    longDescription: "Our Electrical & Electronics ERP Software streamlines operations from component sourcing to final product delivery — with BOM management, production control, quality testing, and compliance tracking built in.",
    image: "/images/industries/electronics.jpg",
    icon: "Zap",
    href: "/industries/electrical-erp-software",
    color: "#F59E0B",
    secondaryColor: "#D97706",
    category: "Manufacturing",
    tags: ["Mx-ERP", "Electronics", "High-Tech"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Without a modern ERP system, electronics businesses struggle with complex production cycles, inventory issues, and quality control challenges.",
    painPoints: [
      { icon: "Layers", title: "Complex BOM & Component Management", description: "Handling multi-level BOMs with thousands of components." },
      { icon: "Scissors", title: "Frequent Design Changes", description: "Engineering changes causing production delays and PCB revisions." },
      { icon: "ShieldAlert", title: "Quality & Testing Issues", description: "Manual testing processes leading to defects and rework." },
      { icon: "Database", title: "Inventory Shortages & Overstocking", description: "Poor demand forecasting impacting stock levels and carrying costs." },
      { icon: "Activity", title: "Disconnected Operations", description: "No synchronization between procurement, production, and sales processes." }
    ],
    capabilitiesHeading: "Solve Every Electronics Industry Challenge with Mx-ERP",
    capabilities: [
      { icon: "List", title: "Advanced BOM & Engineering Change Management", description: "Manage multi-level BOMs and track engineering changes efficiently.", features: ["BOM Versioning", "Change Tracking", "Impact Analysis"] },
      { icon: "Activity", title: "Production Planning & Shop Floor Control", description: "Monitor production schedules, assembly lines, and work orders in real-time.", features: ["Shop Floor Scheduling", "Work Order Management", "Capacity Planning"] },
      { icon: "CheckCircle", title: "Quality Testing & Compliance", description: "Automated QC processes with testing workflows and compliance tracking.", features: ["Automated Testing", "Quality Gates", "Compliance Reporting"] },
      { icon: "Box", title: "Inventory & Component Management", description: "Track electronic components with real-time inventory and shortage alerts.", features: ["Real-time Tracking", "Shortage Alerts", "Automated Reordering"] },
      { icon: "Users", title: "Supply Chain & Vendor Management", description: "Ensure seamless coordination with suppliers and reduce lead time.", features: ["Supplier Portal", "Lead Time Tracking", "Vendor Performance"] },
      { icon: "FileText", title: "Integrated Finance & GST", description: "Complete financial management with GST compliance and reporting.", features: ["Financial Management", "GST Compliance", "Multi-currency Support"] }
    ],
    stats: [
      { value: "38%", label: "increase in production efficiency" },
      { value: "50%", label: "reduction in defects & rework" },
      { value: "1000+", label: "electronics manufacturers served" }
    ],
    caseStudy: {
      quote: "ACGIL's component tracking solved our biggest headache: missing small parts.",
      author: "Amit Shah",
      role: "Factory Manager",
      company: "ElectroNext Systems",
      location: "Gurgaon, India",
      metrics: [
        { value: "28%", label: "inventory accuracy", trend: "up" },
        { value: "40%", label: "downtime reduction", trend: "up" },
        { value: "15%", label: "throughput increase", trend: "up" }
      ]
    },
    relatedIndustries: ["automotive", "aerospace", "metal"],
    dashboard: {
      title: "Electronics Production Hub",
      subtitle: "Real-time SMT line monitoring and SKU tracking",
      kpis: [
        { label: "SMT UPTIME", value: "98.2%", trend: "↗ +1.5%", icon: "Activity", color: "#10b981" },
        { label: "SKU COUNT", value: "42,000", trend: "High Density", icon: "Layers", color: "#3b82f6" },
        { label: "COMPONENT SHORTAGE", value: "12", trend: "Critical", icon: "AlertTriangle", color: "#f59e0b" },
        { label: "QA PASS RATE", value: "99.4%", trend: "↗ +0.8%", icon: "ShieldCheck", color: "#ef4444" }
      ],
      chartTitle: "PRODUCTION YIELD",
      linePath: "M0,24 C20,22 30,14 50,10 C70,8 85,6 100,4",
      areaPath: "M0,24 C20,22 30,14 50,10 C70,8 85,6 100,4 L100,30 L0,30 Z",
      lineColor: "#3b82f6",
      barTitle: "SMT LOAD",
      barColor: "#3b82f6",
      barHeights: [40, 65, 75, 55, 88, 72, 60],
      sidebarItems: [
        { label: "SMT Control", icon: "Cpu" },
        { label: "BOM Manager", icon: "List" },
        { label: "Inventory", icon: "Box" },
        { label: "Engineering Change", icon: "Scissors" },
        { label: "ROHS Reports", icon: "FileText" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Electronics Industry",
      description: "Advanced SMT integration and micro-BOM management for modern electronics plants.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for production, inventory, and performance tracking.", icon: "Thermometer" },
        { title: "IoT & Smart Factory Integration", desc: "Connect machines, sensors, and assembly lines for automation.", icon: "Zap" },
        { title: "Supply Chain Integration", desc: "End-to-end visibility across procurement, manufacturing, and distribution.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      {
        question: "What is Electrical & Electronics ERP Software?",
        answer: "It is an integrated system that helps manage BOM, production, inventory, quality, and supply chain operations in electronics manufacturing."
      },
      {
        question: "Does it support complex BOM structures?",
        answer: "Yes, it supports multi-level BOM management, engineering change control, and complete product lifecycle tracking."
      },
      {
        question: "Can it manage electronic components inventory?",
        answer: "Yes, it provides real-time inventory tracking for electronic components, along with shortage alerts and stock optimization."
      },
      {
        question: "Is it suitable for PCB and assembly units?",
        answer: "Yes, it is designed for PCB manufacturers, electronics assembly units, EMS providers, and other electronics production companies."
      },
      {
        question: "Does it improve quality control?",
        answer: "Yes, it automates quality inspections, testing workflows, and defect tracking to significantly improve product quality."
      }
    ],
    seo: {
      title: "Electrical Industry ERP Software for Manufacturing - ACGIL",
      description: "Electrical ERP software for industry and manufacturing, covering production, inventory, procurement, sales, finance, costing, and business operations.",
      keywords: "electronics erp software, electrical erp software, electronics manufacturing erp, bom management electronics, pcb manufacturing software, inventory management electronics, production erp electronics",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/electrical-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/electrical-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/electrical-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "Electrical Industry ERP Software for Manufacturing - ACGIL",
        description: "Electrical ERP software for industry and manufacturing, covering production, inventory, procurement, sales, finance, costing, and business operations.",
        url: SITE_URL + "/industries/electrical-erp-software",
        type: "website",
        image: SITE_URL + "/images/electronics-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Electrical & Electronics ERP Software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It is an integrated system that helps manage BOM, production, inventory, quality, and supply chain in electronics manufacturing."
      }
    },
    {
      "@type": "Question",
      "name": "Does it support complex BOM structures?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it supports multi-level BOM and engineering change management."
      }
    },
    {
      "@type": "Question",
      "name": "Can it manage electronic components inventory?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it provides real-time tracking of components with shortage alerts."
      }
    },
    {
      "@type": "Question",
      "name": "Is it suitable for PCB and assembly units?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it is ideal for PCB manufacturing, assembly units, and electronics production companies."
      }
    },
    {
      "@type": "Question",
      "name": "Does it improve quality control?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it automates testing processes and reduces defects significantly."
      }
    }
  ]
}`}
  },
  {
    id: "metal",
    slug: "metal-erp-software",
    title: "Metal Fabrication ERP Software",
    shortLabel: "Metal Fabrication",
    tagline: "Smart ERP for Metal Fabrication & Structural Manufacturing Industry",
    description: "Our Metal Fabrication ERP Software streamlines operations from raw material procurement to finished product delivery — with job order management, production control, quality inspection, and compliance tracking built in.",
    longDescription: "Our Metal Fabrication ERP Software streamlines operations from raw material procurement to finished product delivery — with job order management, production control, quality inspection, and compliance tracking built in. Designed for the unique needs of metal fabricators and structural manufacturers.",
    image: "/images/industries/metal-fab.jpg",
    icon: "Wrench",
    href: "/industries/metal-erp-software",
    color: "#7F77DD",
    secondaryColor: "#6D28D9",
    category: "METAL FABRICATION",
    tags: ["Mx-ERP", "Engineering", "Fabrication"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Without a modern ERP system, metal fabrication businesses struggle with complex job costing, material planning, and quality control challenges.",
    painPoints: [
      { icon: "Coins", title: "Complex Job Order & BOM Management", description: "Handling multi-level job orders with material and process routing." },
      { icon: "Calendar", title: "Frequent Design & Specification Changes", description: "Engineering revisions causing production delays and cost overruns." },
      { icon: "Eye", title: "Quality & Inspection Issues", description: "Manual inspection processes leading to rejections and rework." },
      { icon: "Database", title: "Material Shortages & Excess Inventory", description: "Poor demand forecasting impacting raw material stock levels." },
      { icon: "Activity", title: "Disconnected Operations", description: "No synchronization between procurement, production, and dispatch." }
    ],
    capabilitiesHeading: "Solve Every Metal Fabrication Challenge with Mx-ERP",
    capabilities: [
      { icon: "Target", title: "Advanced Job Order & BOM Management", description: "Manage multi-level job orders, material lists, and process routings efficiently.", features: ["Job Order Management", "BOM Control", "Process Routing"] },
      { icon: "Activity", title: "Production Planning & Shop Floor Control", description: "Monitor cutting, welding, bending, and assembly operations in real-time.", features: ["Shop Floor Scheduling", "Work Order Tracking", "Capacity Planning"] },
      { icon: "CheckCircle", title: "Quality Inspection & Compliance", description: "Automated QC workflows with inspection checklists and compliance tracking.", features: ["Automated Inspection", "Quality Checklists", "Compliance Reporting"] },
      { icon: "Database", title: "Raw Material & Inventory Management", description: "Track steel, aluminium, and other materials with real-time stock and shortage alerts.", features: ["Real-time Tracking", "Shortage Alerts", "Automated Reordering"] },
      { icon: "Users", title: "Supply Chain & Vendor Management", description: "Ensure seamless coordination with material suppliers and reduce lead time.", features: ["Supplier Portal", "Lead Time Tracking", "Vendor Management"] },
      { icon: "FileText", title: "Integrated Finance & GST", description: "Complete financial management with job costing, GST compliance, and reporting.", features: ["Job Costing", "GST Compliance", "Financial Reporting"] }
    ],
    stats: [
      { value: "42%", label: "increase in production efficiency" },
      { value: "55%", label: "reduction in material wastage & rework" },
      { value: "800+", label: "metal fabrication units served" }
    ],
    caseStudy: {
      quote: "We finally know exactly what every custom job costs us.",
      author: "Vikram Singh",
      role: "CEO",
      company: "Precision Metal Works",
      location: "Faridabad, India",
      metrics: [
        { value: "22%", label: "margin growth", trend: "up" },
        { value: "18%", label: "scrap reduction", trend: "up" },
        { value: "25%", label: "quote speed", trend: "up" }
      ]
    },
    relatedIndustries: ["automotive", "building", "wood-furniture"],
    dashboard: {
      title: "Metal Shop Control Center",
      subtitle: "Job-wise costing and CNC load balancing",
      kpis: [
        { label: "ACTIVE JOBS", value: "84", trend: "↗ +5", icon: "Target", color: "#10b981" },
        { label: "SCRAP RATE", value: "4.2%", trend: "↘ -1.8%", icon: "TrendingDown", color: "#3b82f6" },
        { label: "CNC UTILIZATION", value: "88%", trend: "Optimal", icon: "Activity", color: "#f59e0b" },
        { label: "QUOTING SPEED", value: "2.4h", trend: "↘ -30%", icon: "Clock", color: "#ef4444" }
      ],
      chartTitle: "MATERIAL EFFICIENCY",
      linePath: "M0,22 C20,18 35,14 55,12 C75,10 90,8 100,6",
      areaPath: "M0,22 C20,18 35,14 55,12 C75,10 90,8 100,6 L100,30 L0,30 Z",
      lineColor: "#f59e0b",
      barTitle: "CNC UTILIZATION",
      barColor: "#f59e0b",
      barHeights: [35, 50, 72, 60, 85, 45, 68],
      sidebarItems: [
        { label: "Job Costing", icon: "Coins" },
        { label: "CNC Schedule", icon: "Activity" },
        { label: "WIP Tracking", icon: "Eye" },
        { label: "Nesting Sync", icon: "LayoutGrid" },
        { label: "MTC Reports", icon: "FileText" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Metal Fabrication Industry",
      description: "Advanced technology solutions for modern metal fabrication plants.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for job progress, material consumption, and performance tracking.", icon: "Thermometer" },
        { title: "IoT & Machine Integration", desc: "Connect CNC machines, laser cutters, and press brakes for automated data capture.", icon: "Zap" },
        { title: "Supply Chain Integration", desc: "End-to-end visibility across procurement, production, and dispatch.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      { question: "What is Metal Fabrication ERP Software?", answer: "It is an integrated system that helps manage job orders, BOM, production, material inventory, quality inspection, and supply chain in metal fabrication businesses." },
      { question: "Does it support complex job order and routing management?", answer: "Yes, it supports multi-level job orders with detailed process routing and operation-wise tracking." },
      { question: "Can it manage raw material inventory like steel and aluminium?", answer: "Yes, it provides real-time tracking of all raw materials with shortage alerts and reorder automation." },
      { question: "Is it suitable for structural fabrication and sheet metal units?", answer: "Yes, it is ideal for structural fabrication shops, sheet metal units, and custom metal manufacturing companies." },
      { question: "Does it improve quality control and reduce rejections?", answer: "Yes, it automates inspection processes and significantly reduces rejections and rework costs." }
    ],
    seo: {
      title: "ERP Software for Metal Manufacturing Industry - ACGIL",
      description: "Metal ERP software for manufacturing and industrial operations, covering production, inventory, procurement, sales, costing, finance, and reporting.",
      keywords: "metal fabrication erp software, fabrication manufacturing erp, job order management software, sheet metal erp, structural fabrication software, raw material inventory management, production erp fabrication, welding fabrication erp",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/metal-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/metal-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/metal-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "ERP Software for Metal Manufacturing Industry - ACGIL",
        description: "Metal ERP software for manufacturing and industrial operations, covering production, inventory, procurement, sales, costing, finance, and reporting.",
        url: SITE_URL + "/industries/metal-erp-software",
        type: "website",
        image: SITE_URL + "/images/metal-fabrication-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Metal Fabrication ERP Software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It is an integrated system that helps manage job orders, BOM, production, material inventory, quality inspection, and supply chain in metal fabrication businesses."
      }
    },
    {
      "@type": "Question",
      "name": "Does it support complex job order and routing management?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it supports multi-level job orders with detailed process routing and operation-wise tracking."
      }
    },
    {
      "@type": "Question",
      "name": "Can it manage raw material inventory like steel and aluminium?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it provides real-time tracking of all raw materials with shortage alerts and reorder automation."
      }
    },
    {
      "@type": "Question",
      "name": "Is it suitable for structural fabrication and sheet metal units?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it is ideal for structural fabrication shops, sheet metal units, and custom metal manufacturing companies."
      }
    },
    {
      "@type": "Question",
      "name": "Does it improve quality control and reduce rejections?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it automates inspection processes and significantly reduces rejections and rework costs."
      }
    }
  ]
}`}
  },
  {
    id: "building",
    slug: "building-erp-software",
    title: "Building Material ERP Software",
    shortLabel: "Building Material",
    tagline: "Smart ERP for Building Material Manufacturing & Distribution Industry",
    description: "Our Building Material ERP Software streamlines operations from raw material procurement to product delivery — with batch production management, inventory control, quality testing, and compliance tracking built in.",
    longDescription: "Our Building Material ERP Software streamlines operations from raw material procurement to product delivery — with batch production management, inventory control, quality testing, and compliance tracking built in. Designed for the heavy demands of the building materials sector, our ERP handles high-volume logistics and complex dealer networks.",
    image: "/images/industries/building.jpg",
    icon: "Building2",
    href: "/industries/building-erp-software",
    color: "#F97316",
    secondaryColor: "#EA580C",
    category: "Manufacturing",
    tags: ["Mx-ERP", "Cement", "Steel", "Ceramics"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Challenges Building Material Manufacturers Face Every Day",
    painPoints: [
      { icon: "Map", title: "Complex Batch Production Management", description: "Handling multi-batch production with varying material compositions and formulas." },
      { icon: "TrendingDown", title: "Frequent Raw Material Price Fluctuations", description: "Volatile input costs impacting pricing, margins, and procurement planning." },
      { icon: "ShieldAlert", title: "Quality & Testing Failures", description: "Manual testing processes leading to substandard products and site rejections." },
      { icon: "Box", title: "Inventory Shortages & Overstocking", description: "Poor demand forecasting causing stockouts or excess inventory at depots." },
      { icon: "Users", title: "Disconnected Sales & Distribution", description: "No real-time sync between orders, dispatch, dealers, and distributors." }
    ],
    capabilitiesHeading: "Solve Every Building Material Industry Challenge with Mx-ERP",
    capabilities: [
      { icon: "Layout", title: "Batch Production & Formula Management", description: "Manage production batches, material compositions, and process workflows efficiently.", features: ["Batch Management", "Formula Control", "Process Workflow"] },
      { icon: "Activity", title: "Production Planning & Plant Floor Control", description: "Monitor mixing, processing, curing, and packaging operations in real-time.", features: ["Shop Floor Scheduling", "Work Order Tracking", "Capacity Planning"] },
      { icon: "CheckCircle", title: "Quality Testing & Compliance", description: "Automated QC processes with lab testing workflows, standards compliance, and certifications tracking.", features: ["Automated Testing", "Quality Standards", "Certification Tracking"] },
      { icon: "Database", title: "Raw Material & Finished Goods Inventory", description: "Track cement, aggregates, chemicals, and finished products with real-time stock and shortage alerts.", features: ["Real-time Tracking", "Shortage Alerts", "Automated Reordering"] },
      { icon: "Users", title: "Dealer, Distributor & Supply Chain Management", description: "Ensure seamless coordination across dealers, distributors, and logistics partners to reduce lead time.", features: ["Dealer Portal", "Distributor Management", "Logistics Coordination"] },
      { icon: "FileText", title: "Integrated Finance & GST", description: "Complete financial management with costing, GST compliance, and reporting.", features: ["Financial Management", "GST Compliance", "Financial Reporting"] }
    ],
    stats: [
      { value: "40%", label: "increase in production efficiency" },
      { value: "50%", label: "reduction in material wastage & rework" },
      { value: "900+", label: "building material manufacturers served" }
    ],
    caseStudy: {
      quote: "We now have 100% clarity on our dealer schemes and logistics costs.",
      author: "Sanjay Gupta",
      role: "CFO",
      company: "EverStrong Cement",
      location: "Indore, India",
      metrics: [
        { value: "15%", label: "freight reduction", trend: "up" },
        { value: "25%", label: "settlement speed", trend: "up" },
        { value: "12%", label: "margin growth", trend: "up" }
      ]
    },
    relatedIndustries: ["metal", "wood-furniture", "packaging"],
    dashboard: {
      title: "Logistics & Dealer Hub",
      subtitle: "Fleet tracking and dealer outstanding monitoring",
      kpis: [
        { label: "FLEET TAT", value: "4.2h", trend: "↘ -15%", icon: "Truck", color: "#10b981" },
        { label: "DEALER OS", value: "₹8.4Cr", trend: "Within Limit", icon: "Users", color: "#3b82f6" },
        { label: "BULK STOCK", value: "12,400T", trend: "↗ +5%", icon: "Mountain", color: "#f59e0b" },
        { label: "PLANT OEE", value: "92%", trend: "↗ +2.1%", icon: "Activity", color: "#ef4444" }
      ],
      chartTitle: "DISPATCH VOLUME",
      linePath: "M0,20 C20,18 35,14 55,10 C70,8 85,14 100,18",
      areaPath: "M0,20 C20,18 35,14 55,10 C70,8 85,14 100,18 L100,30 L0,30 Z",
      lineColor: "#10b981",
      barTitle: "FLEET LOAD",
      barColor: "#10b981",
      barHeights: [45, 60, 38, 72, 55, 80, 48],
      sidebarItems: [
        { label: "Fleet Manager", icon: "Map" },
        { label: "Dealer Portal", icon: "Users" },
        { label: "Bulk Stock", icon: "Database" },
        { label: "Site Delivery", icon: "CheckSquare" },
        { label: "Maintenance", icon: "Settings" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Building Material Industry",
      description: "Advanced technology solutions for modern building material plants.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for production batches, inventory movement, and depot stock tracking.", icon: "Thermometer" },
        { title: "IoT & Plant Integration", desc: "Connect mixers, kilns, conveyor lines, and testing equipment for automated data capture.", icon: "Zap" },
        { title: "Dealer & Distribution Integration", desc: "End-to-end visibility across procurement, plant production, depots, and retail counters.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      { question: "What is Building Material ERP Software?", answer: "It is an integrated system that helps manage batch production, raw material inventory, quality testing, dealer distribution, and supply chain for building material manufacturers and distributors." },
      { question: "Does it support batch production and formula management?", answer: "Yes, it supports multi-batch production with formula-based material composition and process routing." },
      { question: "Can it manage raw material and finished goods inventory across depots?", answer: "Yes, it provides real-time tracking of raw materials and finished goods across plants, warehouses, and depots with automated shortage alerts." },
      { question: "Is it suitable for cement, tiles, paints, and AAC block manufacturers?", answer: "Yes, it is ideal for cement products, tiles, paints, AAC blocks, pipes, aggregates, and all types of building material manufacturers." },
      { question: "Does it improve quality control and reduce site rejections?", answer: "Yes, it automates lab testing and inspection workflows and significantly reduces product rejections and rework costs." }
    ],
    seo: {
      title: "ERP Software for Construction & Building Material Industry | ACGIL",
      description: "ACGIL's construction & building material ERP software manages inventory, purchases, sales, billing, and project costs in one place. Book a free demo today.",
      keywords: "building material erp software, construction material erp, batch production management software, cement erp software, tiles manufacturing erp, paint manufacturing erp, building material inventory management, dealer distribution erp, aac block erp software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/building-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/building-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/building-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "ERP Software for Construction & Building Material Industry | ACGIL",
        description: "ACGIL's construction & building material ERP software manages inventory, purchases, sales, billing, and project costs in one place. Book a free demo today.",
        url: SITE_URL + "/industries/building-erp-software",
        type: "website",
        image: SITE_URL + "/images/building-material-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
   "@context": "https://schema.org",
   "@type": "FAQPage",
   "mainEntity": [
     {
       "@type": "Question",
       "name": "What is Building Material ERP Software?",
       "acceptedAnswer": {
         "@type": "Answer",
         "text": "It is an integrated system that helps manage batch production, raw material inventory, quality testing, dealer distribution, and supply chain for building material manufacturers and distributors."
       }
     },
     {
       "@type": "Question",
       "name": "Does it support batch production and formula management?",
       "acceptedAnswer": {
         "@type": "Answer",
         "text": "Yes, it supports multi-batch production with formula-based material composition and process routing."
       }
     },
     {
       "@type": "Question",
       "name": "Can it manage raw material and finished goods inventory across depots?",
       "acceptedAnswer": {
         "@type": "Answer",
         "text": "Yes, it provides real-time tracking of raw materials and finished goods across plants, warehouses, and depots with automated shortage alerts."
       }
     },
     {
       "@type": "Question",
       "name": "Is it suitable for cement, tiles, paints, and AAC block manufacturers?",
       "acceptedAnswer": {
         "@type": "Answer",
         "text": "Yes, it is ideal for cement products, tiles, paints, AAC blocks, pipes, aggregates, and all types of building material manufacturers."
       }
     },
     {
       "@type": "Question",
       "name": "Does it improve quality control and reduce site rejections?",
       "acceptedAnswer": {
         "@type": "Answer",
         "text": "Yes, it automates lab testing and inspection workflows and significantly reduces product rejections and rework costs."
       }
     }
   ]
 }`,
    }
  },
  {
    id: "wood-furniture",
    slug: "wood-furniture-erp-software",
    title: "Wood & Furniture ERP Software",
    shortLabel: "Wood Furniture",
    tagline: "Smart ERP for Wood & Furniture Manufacturing Industry",
    description: "Our Wood & Furniture ERP Software streamlines operations from timber procurement to finished furniture delivery — with design-based production management, material utilisation, quality control, and order tracking built in.",
    longDescription: "Our Wood & Furniture ERP Software streamlines operations from timber procurement to finished furniture delivery — with design-based production management, material utilisation, quality control, and order tracking built in. From raw timber procurement to finished modular furniture dispatch, our solution optimizes cutting patterns and manages complex product configurations.",
    image: "/images/industries/wood-furniture.jpg",
    icon: "Armchair",
    href: "/industries/wood-furniture-erp-software",
    color: "#8B4513",
    secondaryColor: "#5D2E0A",
    category: "Manufactening",
    tags: ["Mx-ERP", "Furniture", "Woodworking"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Challenges Wood & Furniture Manufacturers Face Every Day",
    painPoints: [
      { icon: "Scissors", title: "Custom Order & Design Management", description: "Handling varied customer designs, sizes, and finish specifications per order." },
      { icon: "TrendingDown", title: "Raw Material & Timber Wastage", description: "Poor material planning leading to excess cutting waste and cost overruns." },
      { icon: "ShieldAlert", title: "Quality & Finishing Issues", description: "Inconsistent finishing, polishing, and upholstery quality across batches." },
      { icon: "Box", title: "Inventory Shortages & Overstocking", description: "Inaccurate demand forecasting causing shortages of wood, hardware, and fabric." },
      { icon: "Activity", title: "Disconnected Sales & Production", description: "No real-time sync between custom orders, workshop production, and delivery schedules." }
    ],
    capabilitiesHeading: "Solve Every Wood & Furniture Industry Challenge with Mx-ERP",
    capabilities: [
      { icon: "Layout", title: "Custom Order & Design-Based Production Management", description: "Manage customer-specific designs, dimensions, material selections, and finish requirements efficiently.", features: ["Design Management", "Material Selection", "Finish Requirements"] },
      { icon: "Activity", title: "Production Planning & Workshop Floor Control", description: "Monitor cutting, assembly, polishing, upholstery, and packing operations in real-time.", features: ["Shop Floor Scheduling", "Work Order Tracking", "Capacity Planning"] },
      { icon: "CheckCircle", title: "Quality Inspection & Finishing Control", description: "Automated QC workflows with stage-wise inspection for surface finishing, joinery, and upholstery quality.", features: ["Automated Inspection", "Stage-wise Quality Control", "Finishing Quality Tracking"] },
      { icon: "Database", title: "Timber, Hardware & Fabric Inventory Management", description: "Track wood, laminates, hardware fittings, and fabric with real-time stock levels and shortage alerts.", features: ["Real-time Tracking", "Shortage Alerts", "Automated Reordering"] },
      { icon: "Users", title: "Dealer, Retailer & Supply Chain Management", description: "Ensure seamless coordination across dealers, retailers, and logistics partners to reduce delivery lead time.", features: ["Dealer Portal", "Retailer Management", "Logistics Coordination"] },
      { icon: "FileText", title: "Integrated Finance & GST", description: "Complete financial management with job costing, GST compliance, and profitability reporting.", features: ["Job Costing", "GST Compliance", "Profitability Reporting"] }
    ],
    stats: [
      { value: "38%", label: "increase in production efficiency" },
      { value: "52%", label: "reduction in material wastage & rework" },
      { value: "700+", label: "wood & furniture manufacturers served" }
    ],
    caseStudy: {
      quote: "The cutting optimizer integration alone paid for the ERP in six months.",
      author: "Rahul Anand",
      role: "Managing Director",
      company: "Modern Spaces Furniture",
      location: "Gurgaon, India",
      metrics: [
        { value: "18%", label: "waste reduction", trend: "up" },
        { value: "40%", label: "config speed", trend: "up" },
        { value: "25%", label: "margin growth", trend: "up" }
      ]
    },
    relatedIndustries: ["metal", "building", "packaging"],
    dashboard: {
      title: "Furniture Production Suite",
      subtitle: "Cutting optimization and assembly tracking",
      kpis: [
        { label: "BOARD YIELD", value: "94.5%", trend: "↗ +3.2%", icon: "Scissors", color: "#10b981" },
        { label: "ACTIVE ORDERS", value: "156", trend: "↗ +12", icon: "Sliders", color: "#3b82f6" },
        { label: "ASSEMBLY STATUS", value: "82%", trend: "On Track", icon: "Layers", color: "#f59e0b" },
        { label: "INSTALLATIONS", value: "24", trend: "Scheduled Today", icon: "Calendar", color: "#ef4444" }
      ],
      chartTitle: "PRODUCTION THROUGHPUT",
      linePath: "M0,24 C18,20 32,14 52,10 C72,6 88,10 100,8",
      areaPath: "M0,24 C18,20 32,14 52,10 C72,6 88,10 100,8 L100,30 L0,30 Z",
      lineColor: "#7F77DD",
      barTitle: "BOARD YIELD",
      barColor: "#7F77DD",
      barHeights: [60, 75, 50, 88, 42, 70, 65],
      sidebarItems: [
        { label: "Cutting Plan", icon: "Layout" },
        { label: "Configurator", icon: "Sliders" },
        { label: "Assembly Line", icon: "Eye" },
        { label: "Installations", icon: "Calendar" },
        { label: "Hardware Lab", icon: "Box" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Wood & Furniture Industry",
      description: "Advanced technology solutions for modern wood & furniture plants.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for order progress, workshop utilisation, and inventory movement tracking.", icon: "Thermometer" },
        { title: "IoT & Machine Integration", desc: "Connect CNC routers, edge banding machines, and finishing lines for automated production data capture.", icon: "Zap" },
        { title: "Dealer & Retail Integration", desc: "End-to-end visibility across procurement, workshop production, warehouses, and showroom inventory.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      { question: "What is Wood & Furniture ERP Software?", answer: "It is an integrated system that helps manage custom orders, design-based production, timber and material inventory, quality inspection, dealer distribution, and supply chain for wood and furniture manufacturers." },
      { question: "Does it support custom and made-to-order furniture production?", answer: "Yes, it supports design-based job orders with customer-specific dimensions, material choices, and finish specifications." },
      { question: "Can it manage timber, hardware, and fabric inventory together?", answer: "Yes, it provides real-time tracking of all raw materials including wood, laminates, hardware fittings, and upholstery fabric with shortage alerts." },
      { question: "Is it suitable for modular furniture, wooden door, and panel manufacturers?", answer: "Yes, it is ideal for modular furniture makers, wooden door and window manufacturers, panel furniture units, and custom interior furniture workshops." },
      { question: "Does it improve finishing quality and reduce rejections?", answer: "Yes, it automates stage-wise inspection workflows and significantly reduces finishing defects, rejections, and rework costs." }
    ],
    seo: {
      title: "Wood and Furniture Industry ERP Software - ACGIL",
      description: "Wood and furniture ERP software for manufacturing businesses, covering production, inventory, procurement, sales, costing, finance, and business operations.",
      keywords: "wood and furniture erp software, furniture manufacturing erp, custom furniture erp, timber inventory management, modular furniture erp, wooden door manufacturing software, furniture production planning software, workshop management erp, interior furniture erp software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/wood-furniture-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/wood-furniture-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/wood-furniture-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "Wood and Furniture Industry ERP Software - ACGIL",
        description: "Wood and furniture ERP software for manufacturing businesses, covering production, inventory, procurement, sales, costing, finance, and business operations.",
        url: SITE_URL + "/industries/wood-furniture-erp-software",
        type: "website",
        image: SITE_URL + "/images/wood-furniture-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Wood & Furniture ERP Software?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "It is an integrated system that helps manage custom orders, design-based production, timber and material inventory, quality inspection, dealer distribution, and supply chain for wood and furniture manufacturers."
        }
      },
      {
        "@type": "Question",
        "name": "Does it support custom and made-to-order furniture production?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it supports design-based job orders with customer-specific dimensions, material choices, and finish specifications."
        }
      },
      {
        "@type": "Question",
        "name": "Can it manage timber, hardware, and fabric inventory together?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it provides real-time tracking of all raw materials including wood, laminates, hardware fittings, and upholstery fabric with shortage alerts."
        }
      },
      {
        "@type": "Question",
        "name": "Is it suitable for modular furniture, wooden door, and panel manufacturers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it is ideal for modular furniture makers, wooden door and window manufacturers, panel furniture units, and custom interior furniture workshops."
        }
      },
      {
        "@type": "Question",
        "name": "Does it improve finishing quality and reduce rejections?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it automates stage-wise inspection workflows and significantly reduces finishing defects, rejections, and rework costs."
        }
      }
    ]
  }`
    }
  },
  {
    id: "aerospace",
    slug: "aerospace-erp-software",
    title: "Aerospace & Defence ERP Software",
    shortLabel: "Aerospace Defence",
    tagline: "Smart ERP for Aerospace & Defence Manufacturing Industry",
    description: "Our Aerospace & Defence ERP Software streamlines operations from component sourcing to final assembly and MRO — with multi-level BOM management, stringent quality control, regulatory compliance, and traceability built in.",
    longDescription: "Our Aerospace & Defence ERP Software streamlines operations from component sourcing to final assembly and MRO — with multi-level BOM management, stringent quality control, regulatory compliance, and traceability built in. Our Aerospace ERP ensures uncompromising quality and traceability, meeting AS9100 and critical defense standards.",
    image: "/images/industries/aerospace.jpg",
    icon: "Rocket",
    href: "/industries/aerospace-erp-software",
    color: "#1E40AF",
    secondaryColor: "#1E3A8A",
    category: "Manufacturing",
    tags: ["Mx-ERP", "Aerospace", "Defense", "AS9100"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Challenges Aerospace & Defence Manufacturers Face Every Day",
    painPoints: [
      { icon: "ShieldCheck", title: "Complex Multi-Level BOM & Configuration Management", description: "Managing highly complex assemblies with thousands of parts, sub-assemblies, and engineering configurations." },
      { icon: "Search", title: "Strict Regulatory & Certification Compliance", description: "Meeting AS9100, ITAR, DCSA, and other aerospace and defence regulatory requirements manually." },
      { icon: "FileText", title: "Traceability & Serialisation Challenges", description: "Inability to trace every component, lot, and serial number across the entire production lifecycle." },
      { icon: "Users", title: "Long Lead Times & Supply Chain Disruptions", description: "Managing multi-tier suppliers with long procurement lead times and critical part shortages." },
      { icon: "Lock", title: "MRO & Asset Lifecycle Management", description: "Tracking maintenance, repair, overhaul schedules, and airworthiness compliance for assets and components." }
    ],
    capabilitiesHeading: "Solve Every Aerospace & Defence Industry Challenge with Mx-ERP",
    capabilities: [
      { icon: "Fingerprint", title: "Multi-Level BOM & Configuration Management", description: "Manage complex multi-level BOMs, engineering change orders, and product configuration control efficiently.", features: ["BOM Management", "Engineering Change Control", "Product Configuration"] },
      { icon: "Activity", title: "Production Planning & Assembly Control", description: "Monitor fabrication, sub-assembly, final assembly, and testing operations with real-time program tracking.", features: ["Fabrication Tracking", "Sub-assembly Monitoring", "Final Assembly Tracking"] },
      { icon: "ShieldCheck", title: "Quality, Compliance & Certification Management", description: "Automated QC workflows with AS9100 compliance, first article inspection, non-conformance reporting, and audit trails.", features: ["Automated QC", "AS9100 Compliance", "Audit Trail Management"] },
      { icon: "Zap", title: "Component Traceability & Serialised Inventory", description: "Track every part, lot, and serial number from procurement to delivery with full traceability and shelf-life management.", features: ["Part Tracking", "Lot Tracking", "Serial Number Tracking"] },
      { icon: "Users", title: "Multi-Tier Supplier & Supply Chain Management", description: "Ensure seamless coordination across approved vendor lists, sub-contractors, and critical part suppliers to reduce lead time.", features: ["Approved Vendor Management", "Sub-contractor Coordination", "Critical Part Management"] },
      { icon: "Lock", title: "MRO & Asset Lifecycle Management", description: "Manage maintenance schedules, repair orders, overhaul tracking, and airworthiness compliance for aircraft and defence assets.", features: ["Maintenance Scheduling", "Repair Order Tracking", "Airworthiness Compliance"] },
      { icon: "FileText", title: "Integrated Finance, Costing & Compliance Reporting", description: "Complete program costing, contract management, and regulatory financial reporting with GST and export compliance.", features: ["Program Costing", "Contract Management", "Regulatory Reporting"] }
    ],
    stats: [
      { value: "45%", label: "increase in production & assembly efficiency" },
      { value: "60%", label: "reduction in compliance failures & audit risks" },
      { value: "1000+", label: "aerospace & defence units served" }
    ],
    caseStudy: {
      quote: "ACGIL made our AS9100 recertification incredibly smooth.",
      author: "Col. Rahul Varma (Retd.)",
      role: "Operations Head",
      company: "AeroPrecision India",
      location: "Hyderabad, India",
      metrics: [
        { value: "100%", label: "audit compliance", trend: "up" },
        { value: "55%", label: "doc efficiency", trend: "up" },
        { value: "20%", label: "lead time reduction", trend: "up" }
      ]
    },
    relatedIndustries: ["electrical", "metal", "automotive"],
    dashboard: {
      title: "Defense Compliance Hub",
      subtitle: "AS9100 tracking and part-level traceability",
      kpis: [
        { label: "COMPLIANCE", value: "100%", trend: "Audit Ready", icon: "ShieldCheck", color: "#10b981" },
        { label: "TRACEABILITY", value: "1:1", trend: "Full History", icon: "Fingerprint", color: "#3b82f6" },
        { label: "DOC STATUS", value: "98.5%", trend: "↗ +12%", icon: "FileCheck", color: "#f59e0b" },
        { label: "SECURE ACCESS", value: "Active", trend: "256-bit AES", icon: "Lock", color: "#ef4444" }
      ],
      chartTitle: "QUALITY METRICS",
      linePath: "M0,20 C20,16 35,12 55,10 C75,8 88,6 100,5",
      areaPath: "M0,20 C20,16 35,12 55,10 C75,8 88,6 100,5 L100,30 L0,30 Z",
      lineColor: "#ef4444",
      barTitle: "COMPLIANCE SCORE",
      barColor: "#ef4444",
      barHeights: [80, 92, 65, 88, 70, 95, 78],
      sidebarItems: [
        { label: "Traceability", icon: "Fingerprint" },
        { label: "Compliance", icon: "FileCheck" },
        { label: "Data Vault", icon: "Database" },
        { label: "ASL Manager", icon: "Users" },
        { label: "Project Costing", icon: "BarChart" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Aerospace & Defence Industry",
      description: "Advanced technology solutions for modern aerospace & defence plants.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for program progress, component traceability, supplier performance, and compliance status tracking.", icon: "Thermometer" },
        { title: "IoT & Smart Factory Integration", desc: "Connect CNC machining centres, CMM inspection systems, and assembly lines for real-time production data capture and predictive maintenance.", icon: "Zap" },
        { title: "Digital Thread & PLM Integration", desc: "End-to-end digital thread connecting design, engineering, production, MRO, and lifecycle data across the program.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      { question: "What is Aerospace & Defence ERP Software?", answer: "It is an integrated system that helps manage multi-level BOM, production programs, component traceability, quality compliance, MRO, and supply chain for aerospace manufacturers, defence contractors, and MRO service providers." },
      { question: "Does it support AS9100 and aerospace regulatory compliance?", answer: "Yes, it supports AS9100 quality management workflows, first article inspection, non-conformance reporting, and complete audit trail management to meet aerospace and defence regulatory requirements." },
      { question: "Can it manage full component traceability and serialisation?", answer: "Yes, it provides end-to-end traceability for every part, lot, and serial number from supplier receipt to final delivery, including shelf-life and certification tracking." },
      { question: "Is it suitable for both OEM aerospace manufacturing and MRO operations?", answer: "Yes, it is ideal for aerospace OEM manufacturers, defence equipment producers, MRO service providers, and tier-1 and tier-2 aerospace sub-contractors." },
      { question: "Does it support MRO and asset lifecycle management?", answer: "Yes, it manages complete maintenance schedules, repair orders, overhaul tracking, and airworthiness compliance for aircraft components and defence assets." }
    ],
    seo: {
      title: "ERP Software for Aerospace Manufacturing Industry - Acgil",
      description: "Aerospace ERP software designed for aerospace businesses, with tools for manufacturing, inventory, procurement, quality control, costing, finance, and reporting.",
      keywords: "aerospace erp software, defence erp software, aerospace manufacturing erp, as9100 erp software, defence manufacturing erp, mro erp software, component traceability erp, aerospace supply chain management, defence contractor erp, aerospace compliance software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/aerospace-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/aerospace-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/aerospace-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "ERP Software for Aerospace Manufacturing Industry - Acgil",
        description: "Aerospace ERP software designed for aerospace businesses, with tools for manufacturing, inventory, procurement, quality control, costing, finance, and reporting.",
        url: SITE_URL + "/industries/aerospace-erp-software",
        type: "website",
        image: SITE_URL + "/images/aerospace-defence-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Aerospace & Defence ERP Software?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "It is an integrated system that helps manage multi-level BOM, production programs, component traceability, quality compliance, MRO, and supply chain for aerospace manufacturers, defence contractors, and MRO service providers."
        }
      },
      {
        "@type": "Question",
        "name": "Does it support AS9100 and aerospace regulatory compliance?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it supports AS9100 quality management workflows, first article inspection, non-conformance reporting, and complete audit trail management to meet aerospace and defence regulatory requirements."
        }
      },
      {
        "@type": "Question",
        "name": "Can it manage full component traceability and serialisation?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it provides end-to-end traceability for every part, lot, and serial number from supplier receipt to final delivery, including shelf-life and certification tracking."
        }
      },
      {
        "@type": "Question",
        "name": "Is it suitable for both OEM aerospace manufacturing and MRO operations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it is ideal for aerospace OEM manufacturers, defence equipment producers, MRO service providers, and tier-1 and tier-2 aerospace sub-contractors."
        }
      },
      {
        "@type": "Question",
        "name": "Does it support MRO and asset lifecycle management?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it manages complete maintenance schedules, repair orders, overhaul tracking, and airworthiness compliance for aircraft components and defence assets."
        }
    ]
  ]
}`}
  },
  {
    id: "hospitality",
    slug: "hospitality-erp-software",
    title: "Hospitality Management ERP Software",
    shortLabel: "Hospitality Services",
    tagline: "Smart ERP for Hospitality & Services Industry",
    description: "Our Hospitality & Services ERP Software streamlines operations from guest reservations to service delivery — with property management, housekeeping control, F&B operations, billing, and compliance tracking built in.",
    longDescription: "Our Hospitality & Services ERP Software streamlines operations from guest reservations to service delivery — with property management, housekeeping control, F&B operations, billing, and compliance tracking built in. From front-office management to banquet sales and back-office finance, our Hospitality ERP provides a unified view of guest experiences and operational efficiency.",
    image: "/images/industries/hospitality.jpg",
    icon: "Bed",
    href: "/industries/hospitality-erp-software",
    color: "#6366F1",
    secondaryColor: "#4338CA",
    category: "Hospitality",
    tags: ["Mx-ERP", "Hotel Management", "Banquet", "CRM"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Challenges Hospitality & Services Businesses Face Every Day",
    painPoints: [
      { icon: "Users", title: "Reservation & Room Management Challenges", description: "Managing walk-ins, online bookings, group reservations, and room allocation manually leading to overbooking and errors." },
      { icon: "Clock", title: "Poor Guest Experience & Service Delays", description: "Lack of real-time coordination between front desk, housekeeping, and F&B causing service delays and guest dissatisfaction." },
      { icon: "Utensils", title: "F&B & Banquet Operations Complexity", description: "Managing restaurant orders, banquet bookings, kitchen operations, and inventory manually leading to wastage and revenue leakage." },
      { icon: "Box", title: "Inventory & Procurement Mismanagement", description: "Poor tracking of housekeeping supplies, F&B ingredients, and maintenance spares causing shortages and excess stock." },
      { icon: "FileText", title: "Disconnected Finance & Billing Operations", description: "No real-time sync between reservations, services consumed, billing, and financial reporting leading to revenue loss." }
    ],
    capabilitiesHeading: "Solve Every Hospitality & Services Challenge with Mx-ERP",
    capabilities: [
      { icon: "Layout", title: "Property & Reservation Management", description: "Manage room bookings, group reservations, check-in/check-out, and room allocation across single or multiple properties in real-time.", features: ["Reservation Management", "Room Allocation", "Check-in/Check-out"] },
      { icon: "Utensils", title: "Housekeeping & Maintenance Scheduling", description: "Monitor housekeeping schedules, room status updates, maintenance requests, and task assignments in real-time.", features: ["Housekeeping Scheduling", "Maintenance Requests", "Task Assignments"] },
      { icon: "TrendingUp", title: "F&B, Restaurant & Banquet Management", description: "Manage restaurant POS, kitchen order tracking, banquet event planning, menu management, and F&B inventory control.", features: ["Restaurant POS", "Kitchen Order Tracking", "Banquet Event Planning"] },
      { icon: "Users", title: "Guest Experience & CRM", description: "Track guest preferences, loyalty programs, feedback management, and personalised service delivery to enhance repeat business.", features: ["Preference Tracking", "Loyalty Programs", "Personalised Service"] },
      { icon: "Box", title: "Inventory & Procurement Management", description: "Real-time tracking of housekeeping supplies, F&B ingredients, and maintenance spares with automated reorder and vendor management.", features: ["Real-time Tracking", "Automated Reordering", "Vendor Management"] },
      { icon: "Layout", title: "Front Office & Channel Management", description: "Seamless integration with OTA channels, travel agents, and walk-in bookings for unified reservation and rate management.", features: ["OTA Integration", "Travel Agent Management", "Direct Booking"] },
      { icon: "FileText", title: "Integrated Finance, Billing & GST", description: "Complete billing management with folio generation, split billing, GST compliance, and consolidated financial reporting.", features: ["Folio Generation", "Split Billing", "GST Compliance"] }
    ],
    stats: [
      { value: "42%", label: "increase in operational efficiency" },
      { value: "55%", label: "reduction in guest complaints & service delays" },
      { value: "600+", label: "hospitality & service businesses served" }
    ],
    caseStudy: {
      quote: "ACGIL's banquet module solved our scheduling chaos. Revenue is up by 25%.",
      author: "Meera Oberoi",
      role: "General Manager",
      company: "The Grand Regency",
      location: "Jaipur, India",
      metrics: [
        { value: "25%", label: "banquet revenue", trend: "up" },
        { value: "30%", label: "checkout speed", trend: "up" },
        { value: "15%", label: "food cost savings", trend: "up" }
      ]
    },
    relatedIndustries: ["food-beverage", "building", "hospitality"],
    dashboard: {
      title: "Guest Experience Hub",
      subtitle: "Occupancy monitoring and banquet sales",
      kpis: [
        { label: "OCCUPANCY", value: "88%", trend: "↗ +5.2%", icon: "Layout", color: "#10b981" },
        { label: "RevPAR", value: "₹6,450", trend: "↗ +12%", icon: "TrendingUp", color: "#3b82f6" },
        { label: "BANQUET SALES", value: "₹12.8L", trend: "8 Events Today", icon: "Star", color: "#f59e0b" },
        { label: "GUEST SAT", value: "4.8/5", trend: "Top Tier", icon: "Smile", color: "#ef4444" }
      ],
      chartTitle: "REVENUE BY DEPARTMENT",
      linePath: "M0,22 C18,20 30,16 50,12 C72,10 85,8 100,6",
      areaPath: "M0,22 C18,20 30,16 50,12 C72,10 85,8 100,6 L100,30 L0,30 Z",
      lineColor: "#f59e0b",
      barTitle: "OCCUPANCY RATE",
      barColor: "#f59e0b",
      barHeights: [55, 68, 78, 60, 85, 45, 70],
      sidebarItems: [
        { label: "Front Desk", icon: "Layout" },
        { label: "Banquet Sales", icon: "Star" },
        { label: "POS Sync", icon: "Utensils" },
        { label: "Housekeeping", icon: "Settings" },
        { label: "Revenue Mgmt", icon: "TrendingUp" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Hospitality & Services Industry",
      description: "Advanced technology solutions for modern hospitality & service businesses.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for occupancy rates, revenue per available room (RevPAR), F&B performance, and guest satisfaction tracking.", icon: "Thermometer" },
        { title: "IoT & Smart Property Integration", desc: "Connect smart room controls, energy management systems, and housekeeping devices for automated property operations.", icon: "Zap" },
        { title: "OTA & Channel Integration", desc: "End-to-end integration with online travel agencies, booking portals, and direct booking engines for unified rate and inventory management.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      { question: "What is Hospitality & Services ERP Software?", answer: "It is an integrated system that helps manage reservations, property operations, housekeeping, F&B, guest experience, billing, and finance for hotels, resorts, service apartments, and hospitality businesses." },
      { question: "Does it support online booking and OTA channel management?", answer: "Yes, it integrates with major online travel agencies and booking portals for real-time rate management, availability updates, and unified reservation management." },
      { question: "Can it manage F&B operations and banquet bookings together?", answer: "Yes, it provides complete F&B management including restaurant POS, kitchen order tracking, banquet event planning, menu management, and F&B inventory control in one system." },
      { question: "Is it suitable for single properties as well as hotel chains?", answer: "Yes, it is ideal for boutique hotels, resorts, service apartments, hotel chains, clubs, and hospitality service businesses of all sizes." },
      { question: "Does it improve guest experience and reduce service delays?", answer: "Yes, it enables real-time coordination between front office, housekeeping, and F&B teams, significantly reducing service delays and improving overall guest satisfaction scores." }
    ],
    seo: {
      title: "ERP Software Solution for Hotels,Restaurant & Hospitality Management | ACGIL",
      description: "Manage hotels, restaurants and hospitality operations efficiently with ACGIL’s integrated ERP solution for finance, inventory, procurement, sales and business reporting.",
      keywords: "hospitality erp software, hotel management erp, hotel erp software, resort management software, property management erp, f&b management software, banquet management erp, guest experience management software, hotel billing software, ota channel management erp, housekeeping management software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/hospitality-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/hospitality-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/hospitality-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "ERP Software Solution for Hotels,Restaurant & Hospitality Management | ACGIL",
        description: "Manage hotels, restaurants and hospitality operations efficiently with ACGIL’s integrated ERP solution for finance, inventory, procurement, sales and business reporting.",
        url: SITE_URL + "/industries/hospitality-erp-software",
        type: "website",
        image: SITE_URL + "/images/hospitality-services-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
  "@context": "https://schema.org",
    "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What is Hospitality & Services ERP Software?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "It is an integrated system that helps manage reservations, property operations, housekeeping, F&B, guest experience, billing, and finance for hotels, resorts, service apartments, and hospitality businesses."
          }
        },
        {
          "@type": "Question",
          "name": "Does it support online booking and OTA channel management?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, it integrates with major online travel agencies and booking portals for real-time rate management, availability updates, and unified reservation management."
          }
        },
        {
          "@type": "Question",
          "name": "Can it manage F&B operations and banquet bookings together?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, it provides complete F&B management including restaurant POS, kitchen order tracking, banquet event planning, menu management, and F&B inventory control in one system."
          }
        },
        {
          "@type": "Question",
          "name": "Is it suitable for single properties as well as hotel chains?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, it is ideal for boutique hotels, resorts, service apartments, hotel chains, clubs, and hospitality service businesses of all sizes."
          }
        },
        {
          "@type": "Question",
          "name": "Does it improve guest experience and reduce service delays?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, it enables real-time coordination between front office, housekeeping, and F&B teams, significantly reducing service delays and improving overall guest satisfaction scores."
          }
    ]
  ]
} `
    }
  },
  {
    id: "packaging",
    slug: "packaging-erp-software",
    title: "Packaging & Plastic ERP Software",
    shortLabel: "Packaging Plastic",
    tagline: "Smart ERP for Packaging & Plastic Manufacturing Industry",
    description: "Our Packaging & Plastic ERP Software streamlines operations from raw material procurement to finished product dispatch — with recipe & formula management, machine-wise production control, quality testing, and compliance tracking built in.",
    longDescription: "Our Packaging & Plastic ERP Software streamlines operations from raw material procurement to finished product dispatch — with recipe & formula management, machine-wise production control, quality testing, and compliance tracking built in. Our Packaging ERP is built for high-speed manufacturing environments, focusing on material efficiency and mould life management.",
    image: "/images/industries/packaging.jpg",
    icon: "Package",
    href: "/industries/packaging-erp-software",
    color: "#EC4899",
    secondaryColor: "#DB2777",
    category: "Manufacturing",
    tags: ["Mx-ERP", "Packaging", "Plastics", "OEE"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Challenges Packaging & Plastic Manufacturers Face Every Day",
    painPoints: [
      { icon: "List", title: "Complex Recipe & Formula Management", description: "Managing multiple product variants with different polymer blends, additives, colours, and thickness specifications per customer order." },
      { icon: "TrendingDown", title: "High Raw Material Price Volatility", description: "Frequent fluctuations in resin, polymer, and additive prices impacting production costs and profit margins." },
      { icon: "CheckCircle", title: "Quality & Rejection Issues", description: "Inconsistent product dimensions, thickness, and print quality leading to customer rejections and rework." },
      { icon: "Activity", title: "Machine Downtime & Utilisation Losses", description: "Poor machine scheduling and unplanned downtime causing production delays and capacity underutilisation." },
      { icon: "Users", title: "Disconnected Customer Orders & Production", description: "No real-time sync between customer packaging specifications, production schedules, and dispatch planning." }
    ],
    capabilitiesHeading: "Solve Every Packaging & Plastic Industry Challenge with Mx-ERP",
    capabilities: [
      { icon: "List", title: "Recipe, Formula & Product Specification Management", description: "Manage customer-specific product formulas, polymer blends, colour recipes, and dimensional specifications efficiently across all product variants.", features: ["Formula Management", "Polymer Blends", "Colour Recipes"] },
      { icon: "Activity", title: "Machine-Wise Production Planning & Shop Floor Control", description: "Monitor blow moulding, injection moulding, extrusion, thermoforming, and printing operations with real-time machine-wise production tracking.", features: ["Machine-wise Tracking", "Production Tracking", "Shop Floor Control"] },
      { icon: "CheckCircle", title: "Quality Testing & Compliance Management", description: "Automated QC workflows with dimension checking, thickness testing, print quality inspection, and customer-specific quality compliance tracking.", features: ["Dimension Checking", "Thickness Testing", "Print Quality Inspection"] },
      { icon: "Database", title: "Raw Material & Finished Goods Inventory Management", description: "Track resins, polymers, masterbatches, additives, and finished goods with real-time stock levels, shelf-life management, and shortage alerts.", features: ["Real-time Tracking", "Shelf-life Management", "Shortage Alerts"] },
      { icon: "Users", title: "Customer Order & Artwork Management", description: "Manage customer-specific packaging orders, artwork approvals, design specifications, and delivery schedules in one system.", features: ["Order Management", "Artwork Approvals", "Design Specifications"] },
      { icon: "Users", title: "Supplier & Supply Chain Management", description: "Ensure seamless coordination with resin suppliers, ink vendors, and logistics partners to reduce lead time and procurement costs.", features: ["Supplier Coordination", "Ink Vendor Management", "Logistics Partnership"] },
      { icon: "FileText", title: "Integrated Finance, Costing & GST", description: "Complete job costing, wastage accounting, GST compliance, and profitability reporting per product and customer.", features: ["Job Costing", "Wastage Accounting", "GST Compliance"] }
    ],
    stats: [
      { value: "41%", label: "increase in production efficiency" },
      { value: "53%", label: "reduction in material wastage & rework" },
      { value: "750+", label: "packaging & plastic manufacturers served" }
    ],
    caseStudy: {
      quote: "Mould tracking alone saved us lakhs in unplanned tool repairs.",
      author: "Prakash Iyer",
      role: "Operations Director",
      company: "FlexiPack Solutions",
      location: "Chennai, India",
      metrics: [
        { value: "15%", label: "uptime growth", trend: "up" },
        { value: "20%", label: "mould life", trend: "up" },
        { value: "10%", label: "material efficiency", trend: "up" }
      ]
    },
    relatedIndustries: ["food-beverage", "chemical", "automotive"],
    dashboard: {
      title: "Packaging Ops Monitor",
      subtitle: "Machine OEE and mould lifecycle tracking",
      kpis: [
        { label: "LINE OEE", value: "84.5%", trend: "↗ +2.8%", icon: "Activity", color: "#10b981" },
        { label: "REGRIND RATIO", value: "12%", trend: "Optimal", icon: "RefreshCw", color: "#3b82f6" },
        { label: "MOULD SHOTS", value: "450K", trend: "Service Due", icon: "Target", color: "#f59e0b" },
        { label: "PRINT YIELD", value: "98.2%", trend: "↗ +1.5%", icon: "CheckCircle", color: "#ef4444" }
      ],
      chartTitle: "WASTE REDUCTION TREND",
      linePath: "M0,20 C18,18 32,14 52,10 C72,8 88,12 100,15",
      areaPath: "M0,20 C18,18 32,14 52,10 C72,8 88,12 100,15 L100,30 L0,30 Z",
      lineColor: "#059A8F",
      barTitle: "LINE OEE",
      barColor: "#059A8F",
      barHeights: [48, 62, 78, 55, 90, 68, 72],
      sidebarItems: [
        { label: "SKU Config", icon: "List" },
        { label: "Mould Manager", icon: "Target" },
        { label: "Regrind Lab", icon: "RefreshCw" },
        { label: "Line Schedule", icon: "Calendar" },
        { label: "Scrap Analytics", icon: "Eye" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Packaging & Plastic Industry",
      description: "Advanced technology solutions for modern packaging & plastic plants.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for machine utilisation, production output, material consumption, rejection rates, and customer order status tracking.", icon: "Thermometer" },
        { title: "IoT & Machine Integration", desc: "Connect blow moulding machines, extruders, injection moulding machines, and printing lines for automated production data capture and predictive maintenance alerts.", icon: "Zap" },
        { title: "Customer & Supply Chain Integration", desc: "End-to-end visibility across customer orders, raw material procurement, production scheduling, and finished goods dispatch.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      { question: "What is Packaging & Plastic ERP Software?", answer: "It is an integrated system that helps manage recipe and formula management, machine-wise production, raw material inventory, quality testing, customer order management, and supply chain for packaging and plastic manufacturers." },
      { question: "Does it support customer-specific product formulas and colour recipes?", answer: "Yes, it manages customer-specific polymer blends, colour masterbatch recipes, additive ratios, and dimensional specifications for every product variant." },
      { question: "Can it track raw material inventory including resins, polymers, and masterbatches?", answer: "Yes, it provides real-time tracking of all raw materials including resins, polymers, masterbatches, additives, and inks with shelf-life management and shortage alerts." },
      { question: "Is it suitable for both rigid and flexible packaging manufacturers?", answer: "Yes, it is ideal for rigid plastic manufacturers such as bottle, cap, and container producers, as well as flexible packaging units producing films, pouches, laminates, and woven sacks." },
      { question: "Does it help reduce material wastage and production rejections?", answer: "Yes, it tracks material consumption at every production stage, automates quality inspection workflows, and significantly reduces wastage, rejections, and rework costs." }
    ],
    seo: {
      title: "Packaging Industry ERP Software | Packaging Manufacturing ERP",
      description: "ACGIL Packaging ERP Software streamlines production, inventory, sales, quality, procurement, and warehouse operations for efficient packaging manufacturing management.",
      keywords: "packaging erp software, plastic manufacturing erp, plastic erp software, packaging manufacturing erp, recipe management erp, blow moulding erp, injection moulding erp, flexible packaging erp, rigid plastic erp, resin inventory management, masterbatch erp software, plastic production planning software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/packaging-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/packaging-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/packaging-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "Packaging Industry ERP Software | Packaging Manufacturing ERP",
        description: "ACGIL Packaging ERP Software streamlines production, inventory, sales, quality, procurement, and warehouse operations for efficient packaging manufacturing management.",
        url: SITE_URL + "/industries/packaging-erp-software",
        type: "website",
        image: SITE_URL + "/images/packaging-plastic-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Packaging & Plastic ERP Software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It is an integrated system that helps manage recipe and formula management, machine-wise production, raw material inventory, quality testing, customer order management, and supply chain for packaging and plastic manufacturers."
      }
    },
    {
      "@type": "Question",
      "name": "Does it support customer-specific product formulas and colour recipes?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it manages customer-specific polymer blends, colour masterbatch recipes, additive ratios, and dimensional specifications for every product variant."
      }
    },
    {
      "@type": "Question",
      "name": "Can it track raw material inventory including resins, polymers, and masterbatches?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it provides real-time tracking of all raw materials including resins, polymers, masterbatches, additives, and inks with shelf-life management and shortage alerts."
      }
    },
    {
      "@type": "Question",
      "name": "Is it suitable for both rigid and flexible packaging manufacturers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it is ideal for rigid plastic manufacturers such as bottle, cap, and container producers, as well as flexible packaging units producing films, pouches, laminates, and woven sacks."
      }
    },
    {
      "@type": "Question",
      "name": "Does it help reduce material wastage and production rejections?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it tracks material consumption at every production stage, automates quality inspection workflows, and significantly reduces wastage, rejections, and rework costs."
      }
    }
  ]
}`
    }
  },
  {
    id: "chemical",
    slug: "chemical-erp-software",
    title: "Chemical & Paints ERP Software",
    shortLabel: "Chemical Paints",
    tagline: "Smart ERP for Chemical & Paints Manufacturing Industry",
    description: "Our Chemical & Paints ERP Software streamlines operations from raw material procurement to finished product dispatch — with batch formula management, hazardous material handling, quality lab testing, and regulatory compliance tracking built in.",
    longDescription: "Our Chemical & Paints ERP Software streamlines operations from raw material procurement to finished product dispatch — with batch formula management, hazardous material handling, quality lab testing, and regulatory compliance tracking built in. Our Chemical ERP handles the complexities of process manufacturing, including formula versioning and hazardous material tracking.",
    image: "/images/industries/chemical.jpg",
    icon: "FlaskConical",
    href: "/industries/chemical-erp-software",
    color: "#10B981",
    secondaryColor: "#059669",
    category: "Manufacturing",
    tags: ["Mx-ERP", "Chemical", "Paint", "Compliance"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Challenges Chemical & Paints Manufacturers Face Every Day",
    painPoints: [
      { icon: "Beaker", title: "Complex Batch Formula & Recipe Management", description: "Managing hundreds of product formulations with varying raw material ratios, pigments, solvents, and additives per product grade and customer specification." },
      { icon: "ShieldAlert", title: "Hazardous Material Handling & Compliance", description: "Ensuring safe storage, handling, and disposal of hazardous chemicals in compliance with regulatory and environmental standards." },
      { icon: "FileText", title: "Strict Quality Lab Testing Requirements", description: "Manual lab testing processes for viscosity, pH, colour matching, and chemical composition leading to batch failures and rework." },
      { icon: "TrendingDown", title: "Raw Material Price Volatility & Shelf-Life Management", description: "Frequent fluctuations in solvent, resin, and pigment prices combined with short shelf-life materials causing procurement and inventory challenges." },
      { icon: "Users", title: "Disconnected Production & Regulatory Reporting", description: "No real-time sync between batch production, safety data sheets, regulatory submissions, and financial reporting." }
    ],
    capabilitiesHeading: "Solve Every Chemical & Paints Industry Challenge with Mx-ERP",
    capabilities: [
      { icon: "Zap", title: "Batch Formula & Recipe Management", description: "Manage product-wise batch formulas, raw material ratios, pigment and solvent compositions, and grade-wise specifications with version control and change history.", features: ["Batch Formulas", "Raw Material Ratios", "Grade-wise Specifications"] },
      { icon: "Activity", title: "Batch Production Planning & Shop Floor Control", description: "Monitor mixing, blending, reaction, filling, and packaging operations with real-time batch-wise production tracking and yield management.", features: ["Batch Production Tracking", "Yield Management", "Process Monitoring"] },
      { icon: "CheckCircle", title: "Quality Lab Testing & Compliance Management", description: "Automated QC workflows with lab test scheduling, viscosity, pH, colour matching, and chemical composition testing with certificate of analysis generation and batch release control.", features: ["Lab Test Scheduling", "Viscosity Testing", "Certificate of Analysis Generation"] },
      { icon: "ShieldCheck", title: "Hazardous Material & Safety Data Sheet Management", description: "Track hazardous raw materials and finished goods with safety data sheet management, storage compliance, handling guidelines, and regulatory reporting.", features: ["Safety Data Sheet Management", "Storage Compliance", "Handling Guidelines"] },
      { icon: "Database", title: "Raw Material & Finished Goods Inventory Management", description: "Track solvents, resins, pigments, additives, and finished products with real-time stock levels, shelf-life and expiry management, and shortage alerts.", features: ["Real-time Tracking", "Shelf-life Management", "Shortage Alerts"] },
      { icon: "Users", title: "Supplier & Supply Chain Management", description: "Ensure seamless coordination with chemical raw material suppliers and logistics partners with approved vendor management and procurement cost control.", features: ["Supplier Coordination", "Approved Vendor Management", "Procurement Cost Control"] },
      { icon: "FileText", title: "Integrated Finance, Batch Costing & GST", description: "Complete batch-wise costing, wastage accounting, GST compliance, and profitability reporting per product, grade, and customer.", features: ["Batch Costing", "Wastage Accounting", "GST Compliance"] }
    ],
    stats: [
      { value: "43%", label: "increase in batch production efficiency" },
      { value: "56%", label: "reduction in quality failures & rework" },
      { value: "800+", label: "chemical & paint manufacturers served" }
    ],
    caseStudy: {
      quote: "The formula management module reduced our batch errors by 90%.",
      author: "Dr. Anjali Rao",
      role: "Technical Director",
      company: "Apex Paints",
      location: "Ahmedabad, India",
      metrics: [
        { value: "90%", label: "error reduction", trend: "up" },
        { value: "35%", label: "batch speed", trend: "up" },
        { value: "15%", label: "raw material savings", trend: "up" }
      ]
    },
    relatedIndustries: ["food-beverage", "packaging", "automotive"],
    dashboard: {
      title: "Chemical Batch Control",
      subtitle: "Formula versioning and lab sync",
      kpis: [
        { label: "COMPLIANCE", value: "99.9%", trend: "Safe", icon: "ShieldCheck", color: "#10b981" },
        { label: "ACTIVE FORMULAS", value: "245", trend: "v4.2 Live", icon: "Zap", color: "#3b82f6" },
        { label: "LAB STATUS", value: "12 Samples", trend: "Pending", icon: "Activity", color: "#f59e0b" },
        { label: "REACTOR LOAD", value: "78%", trend: "Optimal", icon: "Calendar", color: "#ef4444" }
      ],
      chartTitle: "BATCH YIELD ANALYSIS",
      linePath: "M0,18 C18,16 30,20 50,14 C70,12 85,10 100,8",
      areaPath: "M0,18 C18,16 30,20 50,14 C70,12 85,10 100,8 L100,30 L0,30 Z",
      lineColor: "#7F77DD",
      barTitle: "REACTOR LOAD",
      barColor: "#7F77DD",
      barHeights: [42, 58, 70, 80, 55, 65, 75],
      sidebarItems: [
        { label: "Formula Lab", icon: "Zap" },
        { label: "Safety Docs", icon: "ShieldCheck" },
        { label: "Lab Sync", icon: "Activity" },
        { label: "Reactor Ops", icon: "Calendar" },
        { label: "Asset Tracking", icon: "Box" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Chemical & Paints Industry",
      description: "Advanced technology solutions for modern chemical & paints plants.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for batch production output, raw material consumption, lab test results, yield rates, and customer order status tracking.", icon: "Thermometer" },
        { title: "IoT & Plant Integration", desc: "Connect reactors, mixers, blending vessels, filling lines, and lab instruments for automated batch data capture, process monitoring, and predictive maintenance alerts.", icon: "Zap" },
        { title: "Regulatory & Compliance Integration", desc: "End-to-end integration for safety data sheet management, environmental compliance reporting, and hazardous material tracking across the supply chain.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      { question: "What is Chemical & Paints ERP Software?", answer: "It is an integrated system that helps manage batch formulas, production planning, hazardous material compliance, lab quality testing, raw material inventory, and supply chain for chemical and paint manufacturers." },
      { question: "Does it support batch formula and recipe version control?", answer: "Yes, it manages product-wise batch formulas with raw material ratios, pigment compositions, solvent blends, and grade-wise specifications along with complete version control and change history tracking." },
      { question: "Can it manage hazardous material handling and safety data sheets?", answer: "Yes, it tracks hazardous raw materials and finished goods with safety data sheet management, storage compliance, handling guidelines, and regulatory reporting to meet environmental and safety standards." },
      { question: "Does it support lab testing and certificate of analysis generation?", answer: "Yes, it automates lab test scheduling and manages viscosity, pH, colour matching, and chemical composition testing with automated certificate of analysis generation and batch release control." },
      { question: "Is it suitable for specialty chemicals, industrial coatings, and decorative paint manufacturers?", answer: "Yes, it is ideal for specialty chemical producers, industrial coating manufacturers, decorative paint companies, adhesive manufacturers, and other process chemical industries." }
    ],
    seo: {
      title: "Chemical Industry ERP Software | Chemical Manufacturing ERP",
      description: "ACGIL Chemical ERP Software streamlines production, inventory, quality, procurement, compliance, and supply chain operations for chemical manufacturers.",
      keywords: "chemical erp software, paints erp software, chemical manufacturing erp, batch formula management erp, paint manufacturing erp, hazardous material management software, lab quality testing erp, specialty chemical erp, industrial coating erp software, process chemical erp, solvent manufacturing erp, certificate of analysis software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/chemical-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/chemical-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/chemical-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "Chemical Industry ERP Software | Chemical Manufacturing ERP",
        description: "ACGIL Chemical ERP Software streamlines production, inventory, quality, procurement, compliance, and supply chain operations for chemical manufacturers.",
        url: SITE_URL + "/industries/chemical-erp-software",
        type: "website",
        image: SITE_URL + "/images/chemical-paints-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Chemical & Paints ERP Software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It is an integrated system that helps manage batch formulas, production planning, hazardous material compliance, lab quality testing, raw material inventory, and supply chain for chemical and paint manufacturers."
      }
    },
    {
      "@type": "Question",
      "name": "Does it support batch formula and recipe version control?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it manages product-wise batch formulas with raw material ratios, pigment compositions, solvent blends, and grade-wise specifications along with complete version control and change history tracking."
      }
    },
    {
      "@type": "Question",
      "name": "Can it manage hazardous material handling and safety data sheets?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it tracks hazardous raw materials and finished goods with safety data sheet management, storage compliance, handling guidelines, and regulatory reporting to meet environmental and safety standards."
      }
    },
    {
      "@type": "Question",
      "name": "Does it support lab testing and certificate of analysis generation?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it automates lab test scheduling and manages viscosity, pH, colour matching, and chemical composition testing with automated certificate of analysis generation and batch release control."
      }
    },
    {
      "@type": "Question",
      "name": "Is it suitable for specialty chemicals, industrial coatings, and decorative paint manufacturers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it is ideal for specialty chemical producers, industrial coating manufacturers, decorative paint companies, adhesive manufacturers, and other process chemical industries."
      }
    }
  ]
}`
    }
  },
  {
    id: "automotive",
    slug: "automotive-erp-software",
    title: "Automotive ERP Software",
    shortLabel: "Automotive",
    tagline: "Smart ERP for Automotive Manufacturing & Component Industry",
    description: "Our Automotive ERP Software ensures efficiency, precision, and real-time visibility from procurement to production and final dispatch — with BOM management, shop floor control, and quality compliance built in.",
    longDescription: "Our Automotive ERP Software ensures efficiency, precision, and real-time visibility from procurement to production and final dispatch — with BOM management, shop floor control, and quality compliance built in. Our Automotive ERP solution is designed to handle the rigorous demands of the auto component industry, from multi-level BOM management to JIT delivery schedules and stringent quality compliance.",
    image: "/images/industries/automotive.jpg",
    icon: "Settings",
    href: "/industries/automotive-erp-software",
    color: "#2C78B9",
    secondaryColor: "#1a5fa0",
    category: "Manufacturing",
    tags: ["Mx-ERP", "Automotive", "JIT", "OEM"],
    products: ["manufacturing-erp"],
    painPointsHeading: "Challenges Automotive Manufacturers Face Every Day",
    painPoints: [
      { icon: "AlertTriangle", title: "Complex BOM Management", description: "Multi-level BOM complexity causing production delays." },
      { icon: "Activity", title: "Machine Utilization Issues", description: "No real-time visibility into machine performance and downtime." },
      { icon: "CheckCircle", title: "Quality Defects", description: "Defects reaching customers due to manual inspections." },
      { icon: "Truck", title: "Vendor Dependency Risks", description: "Supplier delays impacting production timelines." },
      { icon: "DollarSign", title: "Disconnected Operations", description: "No integration between shop floor, inventory, and finance." }
    ],
    capabilitiesHeading: "Solve Every Automotive Industry Challenge with Mx-ERP",
    capabilities: [
      { icon: "Layout", title: "Multi-Level BOM Management", description: "Efficiently manage complex BOM structures for automotive production.", features: ["Multi-level BOM", "Resource Allocation", "Capacity Planning"] },
      { icon: "Cpu", title: "Production Planning & Shop Floor Control", description: "Track work orders, machine utilization, and production progress in real-time.", features: ["Real-time Monitoring", "OEE Tracking", "Work Order Management"] },
      { icon: "ShieldCheck", title: "Quality Management System", description: "Automated quality checks, inspections, and compliance tracking.", features: ["In-process Inspection", "PDI Checks", "Non-conformance Tracking"] },
      { icon: "Box", title: "Inventory & Warehouse Management", description: "Real-time inventory tracking with minimum stock alerts and optimization.", features: ["Real-time Tracking", "Minimum Stock Alerts", "Optimization"] },
      { icon: "Users", title: "Vendor & Supply Chain Management", description: "Monitor supplier performance and ensure timely material availability.", features: ["Supplier Performance", "Material Availability", "Procurement Tracking"] },
      { icon: "FileText", title: "Integrated Finance & GST", description: "End-to-end financial management with GST compliance and reporting.", features: ["Financial Management", "GST Compliance", "Reporting"] }
    ],
    stats: [
      { value: "40%", label: "increase in production efficiency" },
      { value: "55%", label: "reduction in operational delays" },
      { value: "1000+", label: "auto component manufacturers served" }
    ],
    caseStudy: {
      quote: "ACGIL transformed our production floor. We now have real-time visibility into every component.",
      author: "Rajesh Kumar",
      role: "Operations Head",
      company: "AutoTech Components",
      location: "Pune, India",
      metrics: [
        { value: "35%", label: "production efficiency", trend: "up" },
        { value: "60%", label: "defect reduction", trend: "up" },
        { value: "28%", label: "cost savings", trend: "up" }
      ]
    },
    relatedIndustries: ["metal", "electrical", "packaging"],
    dashboard: {
      title: "Auto Plant Dashboard",
      subtitle: "JIT supply chain and line OEE",
      kpis: [
        { label: "PLANT OEE", value: "92.4%", trend: "↗ +1.2%", icon: "Activity", color: "#10b981" },
        { label: "JIT STATUS", value: "99.8%", trend: "On Time", icon: "Truck", color: "#3b82f6" },
        { label: "DEFECT RATE", value: "0.04%", trend: "↘ -15%", icon: "ShieldCheck", color: "#f59e0b" },
        { label: "SUPPLIER SCORE", value: "94/100", trend: "Good", icon: "Users", color: "#ef4444" }
      ],
      chartTitle: "PRODUCTION TREND",
      linePath: "M0,22 C20,18 35,14 55,10 C75,8 88,6 100,5",
      areaPath: "M0,22 C20,18 35,14 55,10 C75,8 88,6 100,5 L100,30 L0,30 Z",
      lineColor: "#10b981",
      barTitle: "JIT COMPLIANCE",
      barColor: "#10b981",
      barHeights: [70, 85, 60, 92, 48, 78, 65],
      sidebarItems: [
        { label: "Shop Floor", icon: "Cpu" },
        { label: "BOM Manager", icon: "Layout" },
        { label: "Quality Lab", icon: "ShieldCheck" },
        { label: "Vendor Portal", icon: "Users" },
        { label: "Cost Analysis", icon: "BarChart3" }
      ]
    },
    spotlight: {
      title: "Future-Ready Tech Stack for Modern Automotive Industry",
      description: "Advanced technology solutions for modern automotive plants.",
      items: [
        { title: "Real-time Analytics", desc: "Live dashboards for production, quality, and operational performance.", icon: "Thermometer" },
        { title: "IoT & Machine Integration", desc: "Connect machines and sensors for automated shop floor monitoring.", icon: "Zap" },
        { title: "Supply Chain Integration", desc: "Seamless coordination with vendors, dealers, and logistics partners.", icon: "ShieldCheck" }
      ]
    },
    faqs: [
      { question: "What is Automotive ERP Software?", answer: "Automotive ERP software helps manage production, inventory, supply chain, and quality processes in one integrated system." },
      { question: "Does it support multi-level BOM?", answer: "Yes, it supports complex multi-level BOM management for automotive manufacturing." },
      { question: "Can it track machine utilization?", answer: "Yes, it provides real-time monitoring of machine performance and downtime." },
      { question: "Is it suitable for auto component manufacturers?", answer: "Yes, Mx-ERP is designed for OEMs and auto component manufacturers of all sizes." },
      { question: "Does it improve production efficiency?", answer: "Yes, it automates workflows and provides real-time insights to improve productivity." }
    ],
    seo: {
      title: "Automotive Industry ERP Software | Automotive Manufacturing ERP",
      description: "ACGIL Automotive ERP Software streamlines production, inventory, procurement, quality, sales, and supply chain operations for automotive manufacturers.",
      keywords: "automotive erp software, auto manufacturing erp, erp for auto industry, bom management software, production erp automotive, supply chain automotive erp, inventory management automotive",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      author: "ACGIL",
      publisher: "ACGIL",
      canonical: SITE_URL + "/industries/automotive-erp-software",
      hreflang: [
        { href: SITE_URL + "/industries/automotive-erp-software", hreflang: "en" },
        { href: SITE_URL + "/industries/automotive-erp-software", hreflang: "x-default" }
      ],
      openGraph: {
        title: "Automotive Industry ERP Software | Automotive Manufacturing ERP",
        description: "ACGIL Automotive ERP Software streamlines production, inventory, procurement, quality, sales, and supply chain operations for automotive manufacturers.",
        url: SITE_URL + "/industries/automotive-erp-software",
        type: "website",
        image: SITE_URL + "/images/automotive-erp.jpg"
      },
      viewport: "width=device-width, initial-scale=1.0",
      themeColor: "#0A2540",
      faqSchema: `{
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Automotive ERP Software?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Automotive ERP software helps manage production, inventory, supply chain, and quality processes in one integrated system."
      }
    },
    {
      "@type": "Question",
      "name": "Does it support multi-level BOM?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it supports complex multi-level BOM management for automotive manufacturing."
      }
    },
    {
      "@type": "Question",
      "name": "Can it track machine utilization?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it provides real-time monitoring of machine performance and downtime."
      }
    },
    {
      "@type": "Question",
      "name": "Is it suitable for auto component manufacturers?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, Mx-ERP is designed for OEMs and auto component manufacturers of all sizes."
      }
    },
    {
      "@type": "Question",
      "name": "Does it improve production efficiency?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, it automates workflows and provides real-time insights to improve productivity."
      }
    }
  ]
}`
    }
  },
];

export function getIndustryBySlug(slug: string): Industry | undefined {
  return industries.find((ind) => ind.slug === slug);
}
